'use client';
import Layout from '@/app/layout/MainLayout';
import Banners from '@/app/components/Home/Banners';
import NavBar from '@/app/components/MUI/NavBar';
import { useEffect, useState } from 'react';
import { Box } from '@mui/material';
import NavBarActions from '@/app/components/MUI/NavBarActions';
import Image from 'next/image';
import { isOnboardingCompleted } from '@/app/helpers/appStorageMethod';
import { useUser } from '@/app/hooks/useUser';
import { HomeDataProvider } from '@/app/contexts/home-data-context';
import dynamic from 'next/dynamic';

const OnboardingSlideshow = dynamic(
    () => import('@/app/components/Onboarding/OnboardingSlideshow'),
    { ssr: false }
);

/**
 * AppBridgeSync — component con nằm BÊN TRONG HomeDataProvider.
 *
 * Mục đích: Nhận event 'joysamData' từ Native App, lưu token vào cookie,
 * cập nhật UserContext, rồi gọi refresh() để fetch lại dữ liệu trang chủ
 * với Authorization header đúng.
 *
 * Fix race condition: HomeDataProvider có thể đã fetch dữ liệu (không có token)
 * trước khi Native App gửi event này. Gọi refresh() đảm bảo is_like được
 * trả về chính xác ngay sau khi token sẵn sàng.
 */
export default function HomePage() {
    const [showOnboarding, setShowOnboarding] = useState(false);
    const [isCheckingOnboarding, setIsCheckingOnboarding] = useState(true);
    const { user } = useUser();

    useEffect(() => {
        const checkOnboardingStatus = async () => {
            try {
                const completed = await isOnboardingCompleted();
                if (!completed) {
                    setShowOnboarding(true);
                }
            } catch (error) {
                console.error('Error checking onboarding status:', error);
            } finally {
                setIsCheckingOnboarding(false);
            }
        };

        checkOnboardingStatus();
    }, []);

    return (
        <HomeDataProvider>
            {showOnboarding && !isCheckingOnboarding && (
                <OnboardingSlideshow
                    onComplete={() => {
                        setShowOnboarding(false);
                    }}
                />
            )}
            {!showOnboarding && !isCheckingOnboarding && (
                <Layout
                    sx={{ boxShadow: '0px 0px 120px 0px #00000014' }}
                    navBar={
                        <NavBar
                            title={<TitleComponent />}
                            rightContent={user?.id ? <RightContent user={user} /> : null}
                        />
                    }
                    content={
                        <>
                            {' '}
                            <Banners />{' '}
                        </>
                    }
                />
            )}
        </HomeDataProvider>
    );
}

// 마이채널 헤더와 같은 구성이라 공용 컴포넌트로 옮겼다(NavBarActions).
const RightContent = ({ user }: { user: any }) => <NavBarActions userId={user?.id} />;

const TitleComponent = () => {
    return (
        <Box
            sx={{
                width: '100%',
                height: '20px',
                borderRadius: 8,
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'space-between',
                alignItems: 'center',
            }}
        >
            <Image
                src="/images/logo.png"
                alt="Logo"
                width={98}
                height={24}
                priority
                quality={100}
            />
        </Box>
    );
};
